import { useEffect, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setIsChatBotAvailable } from '../redux/slicers/activitySlicer';
import {
  ADMIN_DASHBOARD,
  ADMIN_MY_PRODUCTS_3DMODAL_V2,
  ADMIN_MY_PRODUCTS_MATERIAL_V2,
  ADMIN_MY_PRODUCTS_ROOMS_V2,
  ADMIN_MY_PRODUCTS_TEXTURE_V2,
  ADMIN_MY_PRODUCTS_UPLOADS_V2,
  ADMIN_MY_PROJECTS_V2,
  COLLECTIONS_ROUTE,
  COMMON_HOME,
  PROPS_ROUTE_V2,
  RENDERS_PREVIEWS_ROUTE_V2,
  REQUESTS_ROUTE,
  REQUESTS_ROUTE_PDP_DETAIL,
  SETTINGS_ROUTE,
  TEMPLATES_ROUTE_V2,
  TUTORIALS_ROUTE,
} from '../constants/routePathConstants';
import { getLocalStorage, hubspotHelpChatModuleIconAvailable } from '../helpers/jsHelper';
import { useUserContext } from '../contexts/UserContext';
import { getAndSetCookieData } from '../helpers/apiHelper';
import { apiGetHubspotVisitorIdentificationToken } from '../helpers/api';
import { LogConsole } from '../helpers/logEvents';
import { CURRENT_USER } from '../constants/projectConstants';
import { isCuratorPage } from '../helpers/urlHelper';

const SHOW_CHATBOT_ON_PAGES = [
  ADMIN_DASHBOARD,
  TUTORIALS_ROUTE,
  SETTINGS_ROUTE,
  REQUESTS_ROUTE,
  ADMIN_MY_PRODUCTS_3DMODAL_V2,
  ADMIN_MY_PRODUCTS_ROOMS_V2,
  ADMIN_MY_PRODUCTS_TEXTURE_V2,
  ADMIN_MY_PRODUCTS_UPLOADS_V2,
  ADMIN_MY_PROJECTS_V2,
  RENDERS_PREVIEWS_ROUTE_V2,
  COLLECTIONS_ROUTE,
  TEMPLATES_ROUTE_V2,
  PROPS_ROUTE_V2,
  ADMIN_MY_PRODUCTS_MATERIAL_V2,
];

const loadHSChatbotPopup = ({ email = null, hsToken = null }) => {
  if (email && hsToken) {
    window.hsConversationsSettings = {
      loadImmediately: true,
      identificationEmail: email,
      identificationToken: hsToken,
    };

    // Wait for the script to fully load and then show the widget
    window.HubSpotConversations?.widget?.load();
    // LogConsole('DEBUG___loadHSChatbotPopup');
  }
};

export const getAndSetHubspotVisitorIdentificationToken = async ({ user = null } = {}) => {
  const token = getAndSetCookieData({ cookieName: 'HSIT' }); // HSIT means Hubspot Identification Token

  const currentUser = user || getLocalStorage(CURRENT_USER, true)?.user;

  if (token) {
    if (currentUser) {
      loadHSChatbotPopup({
        email: currentUser?.email,
        hsToken: token,
      });
    }
    return token;
  }

  if (!currentUser) return null;

  const hsToken = await apiGetHubspotVisitorIdentificationToken({
    data: {
      fname: currentUser?.first_name,
      lname: currentUser?.last_name,
      email: currentUser?.email,
    },
  });

  if (hsToken) {
    getAndSetCookieData({
      cookieName: 'HSIT', // HSIT means Hubspot Identification Token
      cookieData: hsToken,
      cookieExpireTime: 43200000, // 12 hours ( as the hubspot identification token expires in 12 hours )
    });

    loadHSChatbotPopup({
      email: currentUser?.email,
      hsToken,
    });
  }

  return hsToken;
};

export const refreshHubspotChatbot = ({ time = 1000 } = {}) => {
  // This function refresh the HubSpot chatbot widget.
  // We may have different chatbots for different pages (e.g., one for curator pages and another for non-curator pages).
  // This ensures that the appropriate chatbot loads on the correct page.
  if (time > 0) {
    setTimeout(() => window?.HubSpotConversations?.widget?.refresh(), time);
    return;
  }

  window?.HubSpotConversations?.widget?.refresh();
};

export const resetAndReloadHubspotChatbot = ({ time = 1000 } = {}) => {
  // This function refresh the HubSpot chatbot widget.
  // We may have different chatbots for different pages (e.g., one for curator pages and another for non-curator pages).
  // This ensures that the appropriate chatbot loads on the correct page.
  if (time > 0) {
    setTimeout(() => window?.HubSpotConversations?.resetAndReloadWidget(), time);
    return;
  }

  window?.HubSpotConversations?.resetAndReloadWidget();
};

// This code defines a custom hook for managing the HubSpot chatbot popup.
export const useHubspotChatbotPopup = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { currentUser } = useUserContext();
  const currentOrg = currentUser?.organization;
  const [isLoaded, setIsLoaded] = useState(false);
  const [isChatbotInitiated, setIsChatbotInitiated] = useState(false);
  const tokenRef = useRef(null);

  // Check if current route is PDP detail route - calculate early to prevent loading
  const isPdpDetailRoute = () => {
    const PDP_DETAIL_BASE = REQUESTS_ROUTE_PDP_DETAIL.split(':id')[0].replace(/\/+$/, '/');
    return location.pathname.startsWith(PDP_DETAIL_BASE);
  };

  const getAndSetHusbpotIdentificationToken = async () => {
    // STRICT: Don't initialize chatbot on PDP detail routes at all
    if (isPdpDetailRoute()) {
      console.log('Skipping chatbot initialization on PDP detail route');
      return;
    }

    // It retrieves and sets the HubSpot visitor identification token using user data, storing it in cookies for future use.
    // The chatbot is configured to load immediately if the user is authenticated, and the visibility of the chatbot frame is toggled based on the current route.
    const hsToken = await getAndSetHubspotVisitorIdentificationToken({
      user: currentUser?.user,
    });

    if (hsToken) {
      tokenRef.current = hsToken;
      // loadHSChatbotPopup({
      //   email: currentUser?.user?.email,
      //   hsToken,
      // });
      setIsChatbotInitiated(true);
    }
  };

  // Ensure HubSpot script is loaded and wait for completion
  const loadScript = async () => {
    if (window.HubSpotConversations) {
      await getAndSetHusbpotIdentificationToken();
      return;
    }

    const script = document.getElementById('hs-script-loader');
    if (script) {
      script.onload = async () => await getAndSetHusbpotIdentificationToken();
    } else {
      const newScript = document.createElement('script');
      newScript.type = 'text/javascript';
      newScript.async = true;
      newScript.defer = true;
      newScript.src = '//js.hs-scripts.com/6917454.js?nocache=' + new Date().getTime(); // Add timestamp to prevent caching
      newScript.id = 'hs-script-loader';
      document.body.appendChild(newScript);
      newScript.onload = async () => await getAndSetHusbpotIdentificationToken();
      newScript.onerror = () => {
        console.error('Failed to load HubSpot script');
        setTimeout(loadScript, 2000); // Retry after 2 seconds if script fails to load
      };
    }
  };

  useEffect(() => loadScript(), []);
  useEffect(() => loadScript(), [currentOrg]);

  // Immediate effect to hide chatbot on PDP detail routes (prevents flicker on load/refresh)
  useEffect(() => {
    if (isPdpDetailRoute()) {
      // Immediately hide chatbot frame on mount if on PDP detail route
      const hideChatbot = () => {
        const chatbotFrame = document.querySelector('#hubspot-messages-iframe-container');
        if (chatbotFrame) {
          chatbotFrame.style.display = 'none';
          chatbotFrame.classList.add('hide-frame');
        }
      };

      // Run immediately
      hideChatbot();

      // Also run after a short delay to catch late-loading elements
      const timerId = setTimeout(hideChatbot, 100);

      return () => clearTimeout(timerId);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isChatbotInitiated || !tokenRef.current || !currentUser) return;

    // STRICT: Double-check PDP detail route before any chatbot operations
    if (isPdpDetailRoute()) {
      // Immediately hide and remove any existing chatbot elements
      const chatbotFrame = document.querySelector('#hubspot-messages-iframe-container');
      if (chatbotFrame) {
        chatbotFrame.style.display = 'none';
        chatbotFrame.classList.add('hide-frame');
      }
      // Force remove the widget
      if (window.HubSpotConversations?.widget?.remove) {
        window.HubSpotConversations.widget.remove();
      }
      // Set chatbot as unavailable immediately
      dispatch(setIsChatBotAvailable(false));
      return;
    }

    // It dispatches an action to update the chatbot's availability status in the Redux store after a short delay.
    const chatbotFrame = document.querySelector('#hubspot-messages-iframe-container');
    let shouldShowChatbot =
      location.pathname === COMMON_HOME ||
      SHOW_CHATBOT_ON_PAGES.includes(location.pathname) ||
      isCuratorPage();

    // Only load the chatbot popup if the user is on a valid page and the user data is available
    if (shouldShowChatbot && tokenRef.current && currentUser) {
      loadHSChatbotPopup({
        email: currentUser?.user?.email,
        hsToken: tokenRef.current,
      });
    }

    // Show or hide the chatbot frame based on the current path
    if (chatbotFrame) {
      if (shouldShowChatbot) {
        chatbotFrame.style.display = '';
        chatbotFrame.classList.remove('hide-frame');
      } else {
        chatbotFrame.style.display = 'none';
        chatbotFrame.classList.add('hide-frame');
      }
    }

    const timerId = setTimeout(
      () => dispatch(setIsChatBotAvailable(hubspotHelpChatModuleIconAvailable())),
      1000
    );

    return () => {
      if (timerId) clearTimeout(timerId);
    };
  }, [isChatbotInitiated, location.pathname, currentUser]);

  useEffect(() => {
    if (!isChatbotInitiated) return; // Don't proceed if the script is not loaded yet

    // if the HubSpot chatbot iframe is loaded and sets the loading state accordingly, using a MutationObserver to monitor changes in the DOM.
    const checkChatbotLoaded = () => {
      const chatbotIframe = document.querySelector('[id^="hubspot-conversations-iframe"]');
      setIsLoaded(!!chatbotIframe);
    };

    checkChatbotLoaded();

    const observer = new MutationObserver(() => checkChatbotLoaded());

    observer.observe(document.body, { childList: true, subtree: true });

    return () => observer.disconnect();
  }, [isChatbotInitiated]);

  return;
};
